import React, { useState } from 'react';
import { X, FileText, CheckCircle, Coins, FileEdit, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import type { InvoiceDirection, InvoiceStatus } from '../../types';

interface UploadedDocReviewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  onStatusSelect: (status: InvoiceStatus, direction: InvoiceDirection) => Promise<void>;
}

const UploadedDocReviewPopup: React.FC<UploadedDocReviewPopupProps> = ({
  isOpen,
  onClose,
  documentName,
  onStatusSelect
}) => {
  const [selectedDirection, setSelectedDirection] = useState<InvoiceDirection>('outgoing');
  const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus>('ready_to_process');

  if (!isOpen) return null;

  const isIncoming = selectedDirection === 'incoming';

  const statusOptions: { value: InvoiceStatus; label: string; icon: React.ReactNode; color: string }[] = isIncoming
    ? [
        {
          value: 'ready_to_process',
          label: 'Received',
          icon: <FileEdit className="w-4 h-4" />,
          color: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        },
        {
          value: 'confirmed',
          label: 'Approved to Pay',
          icon: <CheckCircle className="w-4 h-4" />,
          color: 'bg-coral-500/20 text-foreground-muted border-coral-500/30'
        },
        {
          value: 'paid',
          label: 'Already Paid',
          icon: <Coins className="w-4 h-4" />,
          color: 'bg-green-500/20 text-green-300 border-green-500/30'
        }
      ]
    : [
        {
          value: 'ready_to_process',
          label: 'Ready to Process',
          icon: <FileEdit className="w-4 h-4" />,
          color: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        },
        {
          value: 'confirmed',
          label: 'Confirmed',
          icon: <CheckCircle className="w-4 h-4" />,
          color: 'bg-coral-500/20 text-foreground-muted border-coral-500/30'
        },
        {
          value: 'paid',
          label: 'Already Paid',
          icon: <Coins className="w-4 h-4" />,
          color: 'bg-green-500/20 text-green-300 border-green-500/30'
        }
      ];

  const handleConfirm = async () => {
    await onStatusSelect(selectedStatus, selectedDirection);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background-secondary border border-coral-500/30 rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-coral-500/20">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-coral-400" />
            <h2 className="text-lg font-semibold text-white">Review Uploaded Document</h2>
          </div>
          <button
            onClick={onClose}
            className="text-coral-400 hover:text-foreground p-1 rounded-lg hover:bg-coral-500/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Document Info */}
          <div className="p-3 rounded-lg bg-coral-500/10 border border-coral-500/20">
            <p className="text-sm text-foreground-secondary truncate">{documentName}</p>
          </div>

          {/* The "Low Extraction Accuracy" warning that used to sit here was
              driven by metadata.validation_score, which the backend never
              produces. It defaulted to 100, so the warning was unreachable and
              the number was always fabricated. Removed rather than faked; see
              the follow-up task for computing a real score. */}

          {/* Direction Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              What type of invoice is this?
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => { setSelectedDirection('outgoing'); setSelectedStatus('ready_to_process'); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                  selectedDirection === 'outgoing'
                    ? 'bg-green-500/20 text-green-300 border-green-500/50 ring-2 ring-green-500/30'
                    : 'bg-coral-500/5 border-coral-500/20 text-coral-400 hover:bg-coral-500/10'
                }`}
              >
                <ArrowUpRight className="w-5 h-5 text-green-400" />
                <div className="text-center">
                  <p className="text-xs font-semibold">Outgoing · Income</p>
                  <p className="text-[10px] opacity-70 mt-0.5">You send it · money in</p>
                  <p className="text-[9px] opacity-50 mt-0.5">Accounts Receivable (AR)</p>
                </div>
              </button>
              <button
                onClick={() => { setSelectedDirection('incoming'); setSelectedStatus('ready_to_process'); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-all ${
                  selectedDirection === 'incoming'
                    ? 'bg-red-500/20 text-red-300 border-red-500/50 ring-2 ring-red-500/30'
                    : 'bg-coral-500/5 border-coral-500/20 text-coral-400 hover:bg-coral-500/10'
                }`}
              >
                <ArrowDownLeft className="w-5 h-5 text-red-400" />
                <div className="text-center">
                  <p className="text-xs font-semibold">Incoming · Expense</p>
                  <p className="text-[10px] opacity-70 mt-0.5">You received it · money out</p>
                  <p className="text-[9px] opacity-50 mt-0.5">Accounts Payable (AP)</p>
                </div>
              </button>
            </div>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground-secondary mb-2">
              Current status
            </label>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedStatus(option.value)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    selectedStatus === option.value
                      ? `${option.color} ring-2 ring-coral-500/50`
                      : 'bg-coral-500/5 border-coral-500/20 text-foreground-muted hover:bg-coral-500/10'
                  }`}
                >
                  {option.icon}
                  <span className="text-sm font-medium">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-coral-500/20">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground-secondary hover:bg-coral-500/10 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-coral-500 hover:bg-coral-600 rounded-lg transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadedDocReviewPopup;
